'use client';

import ReCAPTCHA from 'react-google-recaptcha';
import { forwardRef, useImperativeHandle, useRef, useEffect } from 'react';

interface GoogleCaptchaProps {
  onChange: (_value: string | null) => void;
  error?: string | null;
  touched?: boolean;
  className?: string;
  isDarkTheme?: boolean;
}
export interface GoogleCaptchaRef {
  reset: () => void;
}
const GoogleCaptcha = forwardRef<GoogleCaptchaRef, GoogleCaptchaProps>(
  ({ onChange, error, touched, className, isDarkTheme }, ref) => {
    const captchaRef = useRef<ReCAPTCHA | null>(null);

    // Expose reset function to parent
    useImperativeHandle(ref, () => ({
      reset: () => captchaRef.current?.reset(),
    }));

    useEffect(() => {
      // Set SameSite=None; Secure attributes for reCAPTCHA cookies
      const handleRecaptchaLoad = () => {
        // Force reCAPTCHA to use cross-site compatible cookies
        if (typeof window !== 'undefined' && window.document) {
          const cookies = window.document.cookie.split(';');
          cookies.forEach((_cookie) => {
            if (_cookie.trim().startsWith('recaptcha')) {
              // Clear existing reCAPTCHA cookies to force new ones with proper attributes
              const cookieName = _cookie.split('=')[0].trim();
              window.document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.google.com;`;
            }
          });
        }
      };

      // Apply SameSite=None; Secure to any existing cookies
      if (typeof window !== 'undefined') {
        handleRecaptchaLoad();
      }
    }, []);

    return (
      <div className={`relative mt-2 flex w-full ${className || ''}`}>
        <div className="origin-top-left !scale-[0.6] xs:!scale-[0.9] md:!scale-100">
          <ReCAPTCHA
            ref={captchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
            onChange={onChange}
            theme={isDarkTheme ? 'dark' : 'light'}
            asyncScriptOnLoad={() => {
              // Ensure reCAPTCHA scripts load with proper cross-site attributes
              const scripts = document.querySelectorAll(
                'script[src*="recaptcha"]',
              );
              scripts.forEach((script) => {
                if (script.getAttribute('src')?.includes('google.com')) {
                  script.setAttribute('crossorigin', 'anonymous');
                }
              });
            }}
          />
        </div>

        {touched && error && (
          <div className="absolute left-0 top-full mt-0.5 font-opt text-sm font-normal text-red">
            {error}
          </div>
        )}
      </div>
    );
  },
);

GoogleCaptcha.displayName = 'GoogleCaptcha';
export default GoogleCaptcha;
